import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Box, Typography, Button } from "@mui/material";
import { getAuth } from "firebase/auth";
import { getAuthCentral } from "../../config/FirebaseConfig";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { checkDB } from "../../models/DataFunctions.js";
import addLogDocument from "../addLogDocument"; // Custom log function
import { getOrRefreshFirebaseToken } from "../../config/getFirebaseToken.js";
import DropCell from "../../dropzones/DropCell.js"; // Assuming you have a DropCell component for file display
import { set } from "date-fns";
import Spinner from "../../loading/Spinner.js";
import { useAppContext } from "../../config/AppContext.js";

import SmallSpinner from "../../loading/SmallSpinner.js";
import Icon1 from "../../icons/Icon.js";

import { translateDocumentId } from "../../pages/folder_pages/FolderV2.js";
import { addActivityLog } from "../../config/OnlineStatus.js";
import runWithConcurrency from "./runWithConcurrency.js";

const UploadMultiPDF = ({
  folder,
  docId,
  onUpdate,
  large = false,
  width,
  height,
}) => {
  const [files, setFiles] = useState([]);
  const [isHovered, setIsHovered] = useState(false); // To track hover state
  const [loading, setLoading] = useState(false);
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const onDropAccepted = useCallback(
    async (acceptedFiles) => {
      try {
        setLoading(true);
        const auth = getAuthCentral();
        const currentUser = auth.currentUser;
        const token = await getOrRefreshFirebaseToken();

        if (!currentUser) {
          console.error("User not authenticated");
          return;
        }

        const db = getFirestore();
        const userRef = doc(db, "user", currentUser.uid);
        const userSnapshot = await getDoc(checkDB(userRef));

        if (!userSnapshot.exists()) {
          console.error("User document not found");
          return;
        }

        const org_id = userSnapshot.get("org_id");
        let repo_name = await translateDocumentId(docId);
        await addActivityLog(
          "Documents déposés " + repo_name,
          folder.ref,
          org_id,
          userRef.id,
          "file_drop",
          prefix
        );

        // Append new files to the existing list
        const newFiles = [...files, ...acceptedFiles];
        setFiles(newFiles);

        // Upload a single file (parallelized below via runWithConcurrency).
        const uploadOne = async (file) => {
          const url = prefix + `folders/${folder.id}/documents/${docId}/pdf`;
          const formData = new FormData();
          formData.append("file", file, file.name);

          // Set request headers
          const headers = {
            Authorization: `Bearer ${token}`,
          };
          let start = new Date();

          // Send the request
          const response = await fetch(url, {
            method: "POST",
            headers: headers,
            body: formData,
          });

          // Read the response body only once
          const responseText = await response.text();
          let end = new Date();
          let elapsed = end - start;
          // Add log document (fire-and-forget, do not block other uploads)
          addLogDocument(
            url,
            prefix,
            userRef,
            org_id,
            response.status.toString(),
            folder.id || "",
            "", // Assuming empty for docId
            "", // request body as string
            responseText,
            "POST",
            elapsed
          ).catch((e) => console.error("Error logging document:", e));

          // Check response status

          if (response.ok) {
          } else {
            console.error(
              `Error response for file ${file.name}: ${response.status}`
            );
          }
        };

        // Run uploads in parallel with a max of 4 in flight.
        await runWithConcurrency(acceptedFiles, uploadOne, 4);

        setLoading(false);
        // Trigger the onUpdate callback after all files are uploaded

        onUpdate();
      } catch (error) {
        console.error("Error uploading files:", error);
      }
    },
    [files, folder, docId, onUpdate]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDropAccepted,
    multiple: true, // Allow multiple file selection
    accept: {
      "application/pdf": [], // PDF files
      "application/xml": [], // XML files
      "application/zip": [], // ZIP files
    },
    maxFiles: Infinity,
    maxSize: 52428800,
    multiple: true, // Allow multiple file selection
  });

  const handleDelete = (fileToDelete) => {
    const updatedFiles = files.filter((file) => file !== fileToDelete);
    setFiles(updatedFiles);
  };

  return (
    <div className="flex flex-col gap-2 items-center w-full">
      <Box
        {...getRootProps()}
        onMouseEnter={() => setIsHovered(true)} // Trigger hover effect
        onMouseLeave={() => setIsHovered(false)} // Remove hover effect
        sx={{
          border: large ? "2px dashed" : "1px dashed",
          borderColor: isDragActive ? "primary.main" : "grey.400",
          borderRadius: "8px",
          height: height ? height : large ? "150px" : "30px",
          textAlign: "center",
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          padding: "0px 0px",
          width: width ? width : large ? "400px" : "30px",
          cursor: "pointer",
          backgroundColor: loading
            ? "rgba(0, 0, 0, 0.1)"
            : isDragActive
              ? "rgba(0, 0, 255, 0.1)"
              : isHovered
                ? "rgba(0, 0, 0, 0.03)" // When hovered
                : "transparent", // Default background
        }}
      >
        <input {...getInputProps()} />
        {loading ? (
          <div className="text-h3 text-shadow_50">
            <SmallSpinner loading={true}></SmallSpinner>
          </div>
        ) : isDragActive ? (
          <Icon1 icon_name="join" size={large ? 25 : 15} fill="grey" />
        ) : (
          <Icon1 icon_name="join" size={large ? 25 : 15} fill="grey" />
        )}
      </Box>
    </div>
  );
};

export default UploadMultiPDF;
